/**
 * Shared runtime constants.
 *
 * Every value here is consumed by both the API (server-side enforcement) and the
 * web client (form validation and pre-flight upload checks) so the two never
 * disagree about what is acceptable.
 */
/* -------------------------------------------------------------------------- */
/* Upload limits (spec R1.4)                                                   */
/* -------------------------------------------------------------------------- */
/** Maximum size of the uploaded ZIP archive itself: 50 MB. */
export const UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
/** Maximum number of entries inside the archive. */
export const UPLOAD_MAX_FILES = 500;
/** Maximum uncompressed-to-compressed ratio, guarding against zip bombs. */
export const UPLOAD_MAX_RATIO = 100;
/** Maximum total uncompressed size of all surviving entries: 200 MB. */
export const UPLOAD_MAX_UNCOMPRESSED_BYTES = 200 * 1024 * 1024;
/* -------------------------------------------------------------------------- */
/* Allowed archive contents (spec R1.5)                                        */
/* -------------------------------------------------------------------------- */
/**
 * File extensions kept when unpacking an archive, lowercase and without the
 * leading dot. Anything else is skipped and reported through `droppedFiles`;
 * an unknown extension is never fatal.
 */
export const ALLOWED_UPLOAD_EXTENSIONS = [
    'html',
    'htm',
    'css',
    'js',
    'mjs',
    'json',
    'svg',
    'png',
    'jpg',
    'jpeg',
    'gif',
    'webp',
    'avif',
    'ico',
    'woff',
    'woff2',
    'ttf',
    'otf',
    'mp4',
    'webm',
    'txt',
    'md',
];
/** Extensions treated as navigable screens (agent injection, screen dropdown). */
export const HTML_EXTENSIONS = ['html', 'htm'];
/**
 * Narrows an arbitrary extension (lowercase, no leading dot) to an allowed one.
 * Shared so the API and the upload form apply exactly the same rule.
 */
export function isAllowedUploadExtension(extension) {
    return ALLOWED_UPLOAD_EXTENSIONS.includes(extension);
}
/* -------------------------------------------------------------------------- */
/* Validation bounds (spec "Inputs")                                           */
/* -------------------------------------------------------------------------- */
/** Project and prototype name: 1–120 characters, required. */
export const NAME_MIN_LENGTH = 1;
/** Project and prototype name: 1–120 characters, required. */
export const NAME_MAX_LENGTH = 120;
/** Project and prototype description: 0–500 characters, optional. */
export const DESCRIPTION_MIN_LENGTH = 0;
/** Project and prototype description: 0–500 characters, optional. */
export const DESCRIPTION_MAX_LENGTH = 500;
/** Prototype `sourceTool`: 0–60 characters, optional. */
export const SOURCE_TOOL_MIN_LENGTH = 0;
/** Prototype `sourceTool`: 0–60 characters, optional. */
export const SOURCE_TOOL_MAX_LENGTH = 60;
/** Version notes: 0–500 characters, optional. */
export const VERSION_NOTES_MIN_LENGTH = 0;
/** Version notes: 0–500 characters, optional. */
export const VERSION_NOTES_MAX_LENGTH = 500;
/** Comment body: 1–5000 characters of plain text, required. */
export const COMMENT_BODY_MIN_LENGTH = 1;
/** Comment body: 1–5000 characters of plain text, required. */
export const COMMENT_BODY_MAX_LENGTH = 5000;
/**
 * Display name (Phase 1 `localStorage` prompt) and the denormalised
 * `createdByName` / `authorName` / `resolvedByName` columns: 1–60 characters.
 */
export const DISPLAY_NAME_MIN_LENGTH = 1;
/**
 * Display name (Phase 1 `localStorage` prompt) and the denormalised
 * `createdByName` / `authorName` / `resolvedByName` columns: 1–60 characters.
 */
export const DISPLAY_NAME_MAX_LENGTH = 60;
//# sourceMappingURL=constants.js.map